import {
	AnalyticEvent,
	type BundleAdditionalFragment,
	type BundleQuestionFragment,
	formatDurationToDays,
	match,
	type ReservationVmFragment,
	useAuthorizePaymentMutation,
	useCompleteAdditionalEditMutation,
	useCompleteAttributeEditMutation,
	useConfirmPayment,
	useDeclareReservationAdditionalEditMutation,
	useDeclareReservationAttributeEditMutation,
	useEstimateReservationAdditionalServiceEditQuery,
	useEstimateReservationAttributeEditQuery,
	usePostHog,
	useV2GetBundleByIdQuery,
} from "@stepapp/core";
import {
	PaymentStatus,
	type ReplacementCheckoutInput,
} from "@stepapp/core/apiTypes";
import { captureSentryException } from "@stepapp/sentry";
import { useCallback, useMemo, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import {
	parseAttributesToEstimate,
	parseServicesToEstimate,
} from "../../helpers";
import type { EditReservationQuestionType } from "../../store/types";
import ConfirmDeleteServiceModal from "./ConfirmDeleteServiceModal";
import EditAttributes from "./EditAttributes";
import EditContentContainer from "./EditContentContainer";
import EditServices from "./EditServices";

type StatusType = "success" | "loading" | "";

type EditReservationAdditionProps = {
	questionType: EditReservationQuestionType;
	questionId?: string;
	reservation?: ReservationVmFragment;
	form: UseFormReturn<ReplacementCheckoutInput>;
	setDetailsStatus: (v: StatusType) => void;
	setShowErrorModal: (v: string | undefined) => void;
	reservationId: string;
	handleGoBack: () => void;
	setNewReservationId: (v: string) => void;
};

const EditReservationAddition = ({
	questionType,
	questionId,
	reservation,
	form,
	setDetailsStatus,
	setShowErrorModal,
	reservationId,
	handleGoBack,
	setNewReservationId,
}: EditReservationAdditionProps) => {
	const postHog = usePostHog();
	const { handleSubmit, setValue, watch, reset, formState } = form;
	const values = watch();
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const isEditAdditionalServices = questionType === "services";

	const [declareReservationAdditionalEdit] =
		useDeclareReservationAdditionalEditMutation();
	const [declareReservationAttributeEdit] =
		useDeclareReservationAttributeEditMutation();
	const [completeAttributeEdit] = useCompleteAttributeEditMutation();
	const [completeAdditionalEdit] = useCompleteAdditionalEditMutation();
	const [authorizePayment] = useAuthorizePaymentMutation();
	const stripeConfirmPayment = useConfirmPayment();

	const { data: bundle, loading: bundleLoading } = useV2GetBundleByIdQuery({
		skip: !reservation?.bundle?.id,
		fetchPolicy: "network-only",
		variables: {
			id: reservation?.bundle?.id || "",
		},
	});

	const question = match(questionType)
		.with("services", () =>
			bundle?.v2GetBundleById?.services?.find(
				(item) => item?.id === questionId,
			),
		)
		.with(
			"questions",
			() =>
				bundle?.v2GetBundleById?.questions?.find(
					(item) => item?.id === questionId,
				) ||
				bundle?.v2GetBundleById?.categories?.map((category) =>
					category?.questions?.find((question) => question?.id === questionId),
				)[0],
		)
		.otherwise(() => undefined);

	const includedServiceIds = reservation?.services?.map(
		(service) => service?.service?.id,
	);
	const notIncludedServices = bundle?.v2GetBundleById?.services?.filter(
		(service) => !includedServiceIds?.includes(service.id),
	);

	const oneTimeService = !formatDurationToDays(
		reservation?.subscription?.periodicity?.cycleDuration || "P0D",
	);

	const replacementCheckout = useMemo(() => {
		return { ...values };
	}, [values]);

	const parsedAttributes = parseAttributesToEstimate(
		replacementCheckout?.attributes,
	);
	const parsedServices = parseServicesToEstimate(replacementCheckout?.services);

	const {
		data: attributeEditData,
		loading: attributeEditLoading,
		error: attributeEditError,
	} = useEstimateReservationAttributeEditQuery({
		notifyOnNetworkStatusChange: true,
		skip: isEditAdditionalServices,
		fetchPolicy: "network-only",
		variables: {
			reservationId,
			data: { attributes: parsedAttributes },
		},
	});

	const {
		data: additionalServiceReplaceData,
		loading: additionalServiceReplaceLoading,
		error: estimateAdditionalError,
	} = useEstimateReservationAdditionalServiceEditQuery({
		notifyOnNetworkStatusChange: true,
		fetchPolicy: "network-only",
		skip: !isEditAdditionalServices,
		variables: {
			reservationId,
			data: {
				services: parsedServices,
			},
		},
	});

	const newPrice = isEditAdditionalServices
		? additionalServiceReplaceData?.estimateReservationAdditionalServiceEdit
				?.newPrice
		: attributeEditData?.estimateReservationAttributeEdit?.newPrice;

	const priceDifference = useMemo(
		() => (newPrice ? (newPrice || 0) - (reservation?.price || 0) : 0),
		[newPrice, reservation?.price],
	);

	const priceDetails = {
		currentReservationPrice: reservation?.price,
		priceDifference,
		newPrice,
	};

	const hasEditLimits = oneTimeService && priceDifference < 0;
	const isAnswerRequired =
		question != null && "isRequired" in question && question.isRequired
			? !values?.attributes?.find((a) => a?.attributeId === questionId)
					?.valueId[0]
			: false;

	const isSubmitDisabled =
		hasEditLimits ||
		!formState.isDirty ||
		isAnswerRequired ||
		attributeEditLoading ||
		additionalServiceReplaceLoading;

	const serviceToDeleteName =
		bundle?.v2GetBundleById?.services?.find((item) => item?.id === questionId)
			?.name ||
		reservation?.services?.find((item) => item?.service?.id === questionId)
			?.service?.name;

	const onSubmit = handleSubmit(async (values: ReplacementCheckoutInput) => {
		if (attributeEditError || estimateAdditionalError) {
			const errorMsg =
				attributeEditError?.message ||
				estimateAdditionalError?.message ||
				"estimateReservationError";
			setShowErrorModal(errorMsg);
			return;
		}

		postHog?.capture(AnalyticEvent.Reservation.ChangeServiceDetails);

		const attributes = parseAttributesToEstimate(values?.attributes);
		const services = parseServicesToEstimate(values?.services);

		setDetailsStatus("loading");

		const { error, requiresAction, paymentId, swapId } = await (async () => {
			if (!isEditAdditionalServices) {
				const {
					data: declareAttributeEdit,
					errors: declareAttributeEditErrors,
				} = await declareReservationAttributeEdit({
					variables: {
						reservationId: reservation?.id || "",
						data: {
							attributes,
						},
					},
				});

				return {
					requiresAction:
						declareAttributeEdit?.declareReservationAttributeEdit?.payment
							?.status === PaymentStatus.RequiresAction,
					paymentId:
						declareAttributeEdit?.declareReservationAttributeEdit?.payment
							?.id || "",
					swapId:
						declareAttributeEdit?.declareReservationAttributeEdit?.id || "",
					error: declareAttributeEditErrors,
				};
			}
			const {
				data: declareAdditionalEdit,
				errors: declareAdditionalEditErrors,
			} = await declareReservationAdditionalEdit({
				variables: {
					reservationId: reservation?.id || "",
					data: {
						services,
					},
				},
			});

			return {
				requiresAction:
					declareAdditionalEdit?.declareReservationAdditionalEdit?.payment
						?.status === PaymentStatus.RequiresAction,
				paymentId:
					declareAdditionalEdit?.declareReservationAdditionalEdit?.payment
						?.id || "",
				swapId:
					declareAdditionalEdit?.declareReservationAdditionalEdit?.id || "",
				error: declareAdditionalEditErrors,
			};
		})();

		if (error) {
			captureSentryException("Edit reservation details error", {
				extra: {
					errorMessage: error?.[0]?.message,
					reservationId: reservation?.id,
					question: questionType,
					isSubscription: !oneTimeService,
				},
			});
			setDetailsStatus("");
			return setShowErrorModal(error?.[0]?.message);
		}

		async function completeReservationChanges() {
			const newReservationId = await (async () => {
				if (!isEditAdditionalServices) {
					const { data: completeAttributeData } = await completeAttributeEdit({
						variables: {
							swapId,
						},
						refetchQueries: ["GetReservations"],
					});

					return (
						completeAttributeData?.completeAttributeEdit?.toReservation?.id ||
						""
					);
				}
				const { data: completeAdditionalSwap } = await completeAdditionalEdit({
					variables: {
						swapId,
					},
					refetchQueries: ["GetReservations"],
				});

				return (
					completeAdditionalSwap?.completeAdditionalEdit?.toReservation?.id ||
					""
				);
			})();

			if (newReservationId) {
				setNewReservationId(newReservationId);
				return setDetailsStatus("success");
			}

			setDetailsStatus("");
			setShowErrorModal("completeReservationChangeError");
		}

		// Verification no needed
		if (!requiresAction) {
			completeReservationChanges();
			return;
		}

		const { data: authorizePaymentData } = await authorizePayment({
			variables: {
				paymentId,
			},
		});
		const authorizationRequired =
			authorizePaymentData?.authorizePayment?.status ===
			PaymentStatus.RequiresAction;

		if (!authorizationRequired) {
			completeReservationChanges();
			return;
		}

		const confirmStripePayment = await stripeConfirmPayment(
			authorizePaymentData?.authorizePayment?.stripePaymentNextStep
				?.clientSecret || "",
			paymentId,
			captureSentryException,
		);

		if (confirmStripePayment) {
			completeReservationChanges();
		} else {
			setDetailsStatus("");
			setShowErrorModal("confirmPaymentError");
		}
	});

	const handleSaveChanges = useCallback(() => {
		onSubmit();
	}, [onSubmit]);

	const handleDeleteService = useCallback(async () => {
		setShowDeleteModal(false);
		setDetailsStatus("loading");

		const newServices = replacementCheckout?.services.filter(
			(s) => s?.serviceId !== questionId,
		);

		setValue("services", newServices, { shouldDirty: true, shouldTouch: true });

		postHog?.capture(AnalyticEvent.Reservation.DeleteService, {
			id: reservation?.id || "",
			name: reservation?.bundle?.name || "",
		});

		onSubmit();
	}, [
		questionId,
		replacementCheckout,
		reservation?.id,
		reservation?.bundle?.name,
		postHog,
		setDetailsStatus,
		setValue,
		onSubmit,
	]);

	const handleClose = useCallback(() => {
		handleGoBack();
		setDetailsStatus("");
		reset();
	}, [setDetailsStatus, handleGoBack, reset]);

	return (
		<EditContentContainer
			questionType={questionType}
			handleSaveChanges={handleSaveChanges}
			loading={attributeEditLoading || additionalServiceReplaceLoading}
			handleClose={handleClose}
			isSubmitDisabled={isSubmitDisabled}
			priceDetails={priceDetails}
			categoryType={reservation?.bundle?.categoryType}
		>
			{match({ questionType })
				.with({ questionType: "questions" }, () => (
					<EditAttributes
						loading={bundleLoading}
						setValue={setValue}
						question={question as BundleQuestionFragment}
						attributes={values?.attributes?.filter((a) => !!a)}
						questionId={questionId}
					/>
				))
				.with({ questionType: "services" }, () => (
					<EditServices
						loading={bundleLoading}
						services={values?.services?.filter((s) => !!s)}
						setValue={setValue}
						question={question as BundleAdditionalFragment}
						questionId={questionId}
						additionalServiceTitle={
							bundle?.v2GetBundleById?.additionalTitle || ""
						}
						showCustomAdditionalDetails={
							!!bundle?.v2GetBundleById?.isAdditionalTitleAndDescriptionEnabled
						}
						notIncludedServices={notIncludedServices}
						isOneTimeService={oneTimeService}
						setShowDeleteModal={setShowDeleteModal}
						serviceToDeleteName={serviceToDeleteName}
					/>
				))
				.otherwise(() => null)}

			<ConfirmDeleteServiceModal
				loading={additionalServiceReplaceLoading}
				isVisible={showDeleteModal}
				setIsVisible={setShowDeleteModal}
				serviceName={question?.name || serviceToDeleteName}
				handleDeleteService={handleDeleteService}
			/>
		</EditContentContainer>
	);
};

export default EditReservationAddition;
